import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as PhosphorIcons from '@phosphor-icons/react';
import {
  Basket, Gear, ClipboardText, Package,
  Truck, MapPin,
  UsersThree, PersonSimple, Wheelchair, NavigationArrow,
  Crane, Hammer, ShieldStar, CheckCircle,
  Play, Pause, CaretLeft, CaretRight, Sparkle,
  Leaf, Plant, Drop, TreeStructure,
  Barcode, ShoppingCart, HandCoins,
  Warehouse, PaintBucket,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Résout dynamiquement une icône Phosphor à partir de son nom (string venant du backend)
function resolveIcon(name) {
  if (name && PhosphorIcons[name]) return PhosphorIcons[name];
  return Sparkle;
}

// Illustrations officielles Objectif SCOP Outremer (CDN Emergent)
export const IMG = {
  agro: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/m173i449_AGRO%E2%80%99SCOP%20de%20la%20r%C3%A9colte%20au%20produit%20fini.png',
  logi: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/obfpqfn2_CIPASCOP%20LOGISCOP%20TRANSPORT%20MARCHANDISE.png',
  mobilite: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/lwa86sxi_CIPASCOP%20OSCOP%20MOBILITE.png',
  batiment: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/0uue9l7l_Mission%20coop%C3%A9rative%20%20charpente%20et%20couverture.png',
  horticole: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/aeh8bjsn_Mission%20coop%C3%A9rative%20C.I.P.A.S.C.O.P.%20%E2%80%94%20Exploitation%20horticole.png',
  bois: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/ivhc97fy_Mission%20coop%C3%A9rative%20C.I.P.A.S.C.O.P.%20%E2%80%94%20O%E2%80%99SCOP%20INDUSTRIE%20BOIS.png',
  supermarche: 'https://customer-assets-cm19k8pv.emergentagent.net/job_fastapi-react-crm-4/artifacts/28d97okt_Mission%20coop%C3%A9rative%20C.I.P.A.S.C.O.P.%20au%20supermarch%C3%A9.png',
};

// Composant Avatar cropé à partir d'une illustration officielle.
// Utilise background-image + background-size + background-position pour zoomer sur un visage.
export function IllustrationAvatar({ src, cx, cy, zoom = 5, size = 32, ring = '#2D5A4C', testId }) {
  return (
    <span
      className="inline-block rounded-full bg-cover flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${src})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: `${cx}% ${cy}%`,
        boxShadow: `0 0 0 2px ${ring}, 0 6px 12px -6px rgba(0,0,0,.35)`,
      }}
      data-testid={testId}
      aria-hidden="true"
    />
  );
}

export const MISSIONS = [
  {
    key: 'agro',
    name: "AGRO'SCOP",
    tagline: 'Transformation agroalimentaire',
    description: "De la récolte au produit fini : nos coopérateurs valorisent fruits, tubercules et jus jusqu'à la mise en rayon.",
    accent: '#80C51F',
    gradient: 'from-[#80C51F]/85 via-[#5FA015]/85 to-[#2D5A4C]/90',
    image: IMG.agro,
    steps: [
      { icon: Basket, label: 'Réception & Tri', short: 'Ananas, mangues, bananes locales', name: 'Marie-Claire', crop: { cx: 12, cy: 55 } },
      { icon: Gear, label: 'Préparation & Transformation', short: 'Presses, cuves inox, recettes créoles', name: 'Sarah', crop: { cx: 40, cy: 45 } },
      { icon: ClipboardText, label: 'Contrôle qualité', short: 'Traçabilité, HACCP, labels ESS', name: 'Jean-Michel', crop: { cx: 66, cy: 45 } },
      { icon: Package, label: 'Conditionnement & Stockage', short: 'Confitures, jus, bocaux prêts à vendre', name: 'Élise', crop: { cx: 88, cy: 55 } },
    ],
  },
  {
    key: 'logi',
    name: "LOGI'SCOP",
    tagline: 'Transport de marchandises',
    description: "De l'entrepôt à la livraison finale, une chaîne logistique coopérative pilotée par nos associés.",
    accent: '#00AEEF',
    gradient: 'from-[#00AEEF]/85 via-[#0075A8]/85 to-[#1A2421]/90',
    image: IMG.logi,
    steps: [
      { icon: Package, label: 'Préparation & Chargement', short: 'Palettes filmées, quais organisés', name: 'Ravi', crop: { cx: 10, cy: 52 } },
      { icon: Truck, label: 'Transport routier', short: 'Flotte partagée, chauffeurs coopérateurs', name: 'Anziza', crop: { cx: 32, cy: 55 } },
      { icon: MapPin, label: 'Suivi & Traçabilité', short: 'GPS temps réel, tablette embarquée', name: 'Yannick', crop: { cx: 76, cy: 60 } },
      { icon: Warehouse, label: 'Livraison & Réception', short: 'Bon signé, colis remis en main propre', name: 'Camille', crop: { cx: 90, cy: 55 } },
    ],
  },
  {
    key: 'mobilite',
    name: "O'SCOP MOBILITÉ",
    tagline: 'Transport de personnes',
    description: "Une mobilité inclusive : personnes âgées, personnes en situation de handicap, trajets scolaires et solidaires.",
    accent: '#FF8E00',
    gradient: 'from-[#FF8E00]/85 via-[#EA3C3A]/85 to-[#8B1A18]/90',
    image: IMG.mobilite,
    steps: [
      { icon: UsersThree, label: 'Accueil & Prise en charge', short: 'Écoute, préparation, sécurité', name: 'Léa', crop: { cx: 15, cy: 50 } },
      { icon: PersonSimple, label: 'Transport sécurisé', short: 'Véhicules équipés, coopérateurs formés', name: 'David', crop: { cx: 30, cy: 55 } },
      { icon: Wheelchair, label: 'Mobilité adaptée', short: 'Rampes, PMR, seniors, familles', name: 'Chantal', crop: { cx: 50, cy: 60 } },
      { icon: NavigationArrow, label: 'Suivi & Coordination', short: 'Tournées optimisées, GPS partagé', name: 'Kevin', crop: { cx: 88, cy: 50 } },
    ],
  },
  {
    key: 'batiment',
    name: 'CHARPENTE & COUVERTURE',
    tagline: 'Bâtiment coopératif — construction neuve',
    description: "Nos artisans bâtissent, couvrent et sécurisent les toitures ultramarines, aux normes cyclones.",
    accent: '#EA3C3A',
    gradient: 'from-[#EA3C3A]/85 via-[#8B1A18]/85 to-[#2D5A4C]/90',
    image: IMG.batiment,
    steps: [
      { icon: Crane, label: 'Levage de charpente', short: 'Grue, fermes bois, poutres calibrées', name: 'Thierry', crop: { cx: 5, cy: 55 } },
      { icon: Hammer, label: 'Assemblage & fixations', short: 'Perceuses, connecteurs, sécurité EPI', name: 'Aïcha', crop: { cx: 40, cy: 50 } },
      { icon: ShieldStar, label: 'Étanchéité & couverture', short: 'Tuiles, bacs acier, résistance cyclonique', name: 'Marc', crop: { cx: 78, cy: 40 } },
      { icon: CheckCircle, label: 'Contrôle qualité', short: 'Plans à jour, inspection finale', name: 'Sonia', crop: { cx: 62, cy: 75 } },
    ],
  },
  {
    key: 'horticole',
    name: 'EXPLOITATION HORTICOLE',
    tagline: 'Pépinière & horticulture tropicale',
    description: "De la plantation au conditionnement, nos coopérateurs cultivent hibiscus, plantes vertes et fleurs des tropiques.",
    accent: '#D5007D',
    gradient: 'from-[#D5007D]/85 via-[#80C51F]/85 to-[#2D5A4C]/90',
    image: IMG.horticole,
    steps: [
      { icon: Plant, label: 'Préparation & Plantation', short: 'Terreau, godets, boutures locales', name: 'Anthony', crop: { cx: 15, cy: 55 } },
      { icon: Drop, label: 'Entretien des cultures', short: 'Arrosage, désherbage, taille', name: 'Fabienne', crop: { cx: 40, cy: 50 } },
      { icon: Leaf, label: 'Récolte & Tri', short: 'Hibiscus, plantes vertes, floraison', name: 'Patrick', crop: { cx: 62, cy: 50 } },
      { icon: Package, label: 'Conditionnement', short: 'Emballage bio, transport ferme', name: 'Nadia', crop: { cx: 88, cy: 55 } },
    ],
  },
  {
    key: 'bois',
    name: "O'SCOP INDUSTRIE BOIS",
    tagline: 'Usine de transformation du bois',
    description: "De la grume aux planches usinées, la filière bois coopérative transforme et expédie du bois ultramarin.",
    accent: '#8B4513',
    gradient: 'from-[#8B4513]/85 via-[#5C2A0A]/85 to-[#1A2421]/90',
    image: IMG.bois,
    steps: [
      { icon: TreeStructure, label: 'Réception & Tri du bois', short: 'Grumes, essences locales, quai', name: 'Loïc', crop: { cx: 15, cy: 60 } },
      { icon: Gear, label: 'Sciage & Transformation', short: 'Scies, débit, calibrage', name: 'Malik', crop: { cx: 40, cy: 55 } },
      { icon: ClipboardText, label: 'Usinage & Contrôle qualité', short: 'Mesures, finitions, normes', name: 'Karine', crop: { cx: 62, cy: 55 } },
      { icon: PaintBucket, label: 'Stockage & Expédition', short: 'Palettes, sanglage, camion', name: 'Julien', crop: { cx: 88, cy: 55 } },
    ],
  },
  {
    key: 'supermarche',
    name: 'MISSION SUPERMARCHÉ',
    tagline: 'Appui rayon & commerce coopératif',
    description: "Nos coopérateurs renforcent les équipes en supermarché : rayons, commandes et relation client au quotidien.",
    accent: '#00AEEF',
    gradient: 'from-[#00AEEF]/85 via-[#2D5A4C]/85 to-[#1A2421]/90',
    image: IMG.supermarche,
    steps: [
      { icon: Basket, label: 'Mise en rayon', short: 'Frais, secs, roulement produits', name: 'Amélie', crop: { cx: 42, cy: 50 } },
      { icon: Barcode, label: 'Inventaire', short: 'Scan, stocks, alertes ruptures', name: 'Léonard', crop: { cx: 60, cy: 45 } },
      { icon: ShoppingCart, label: 'Préparation de commandes', short: 'Drive, click & collect, panier client', name: 'Amélie', crop: { cx: 45, cy: 65 } },
      { icon: HandCoins, label: 'Appui commercial', short: 'Conseil client, mise en avant', name: 'Léonard', crop: { cx: 62, cy: 55 } },
    ],
  },
];

const AUTOPLAY_MS = 9000;

export default function MissionsShowcase() {
  const [missions, setMissions] = useState(null); // null = loading, [] = empty
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef(null);

  // Fetch missions depuis le backend (fallback : liste hardcodée locale MISSIONS)
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/missions`)
      .then((r) => { if (alive) setMissions(r.data?.items?.length ? r.data.items : MISSIONS); })
      .catch(() => { if (alive) setMissions(MISSIONS); });
    return () => { alive = false; };
  }, []);

  // Deep link: /#horticole, /#bois, etc. -> ouvrir sur le bon tab
  useEffect(() => {
    if (!missions || missions.length === 0) return;
    const applyHash = () => {
      const raw = (typeof window !== 'undefined' ? window.location.hash : '') || '';
      const key = raw.replace(/^#/, '').toLowerCase();
      if (!key) return;
      const i = missions.findIndex((m) => (m.key || '').toLowerCase() === key);
      if (i >= 0) {
        setIdx(i);
        setPlaying(false);
        setTimeout(() => {
          const el = document.querySelector("[data-testid='missions-showcase']");
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [missions]);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (mq) {
      setReducedMotion(mq.matches);
      const onChange = () => setReducedMotion(mq.matches);
      mq.addEventListener?.('change', onChange);
      return () => mq.removeEventListener?.('change', onChange);
    }
  }, []);

  useEffect(() => {
    if (!missions || !playing || reducedMotion) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % missions.length), AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, playing, reducedMotion, missions]);

  if (!missions) return null;
  if (missions.length === 0) return null;
  const safeIdx = Math.min(idx, missions.length - 1);
  const mission = missions[safeIdx];
  const prev = () => setIdx((i) => (i - 1 + missions.length) % missions.length);
  const next = () => setIdx((i) => (i + 1) % missions.length);

  return (
    <section className="max-w-6xl mx-auto px-6 mt-16 mb-8" data-testid="missions-showcase">
      {/* Titre section */}
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.28em] text-[#2D5A4C] font-bold flex items-center justify-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#80C51F]" />
          Missions coopératives en action
          <Sparkle size={14} weight="fill" className="text-[#80C51F]" />
        </p>
        <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
          Ce que <span className="text-[#2D5A4C]">nos coopérateurs</span> font ensemble
        </h2>
        <p className="mt-2 text-sm text-[#5E6D66] max-w-2xl mx-auto">
          {missions.length} exemples réels de missions <strong>C.I.P.A.S.C.O.P.</strong> organisées et facturées par Objectif SCOP Outremer.
        </p>
      </div>

      {/* Tabs missions */}
      <div className="flex flex-wrap justify-center gap-2 mb-6" role="tablist" data-testid="missions-tabs">
        {missions.map((m, i) => (
          <button
            key={m.key}
            role="tab"
            aria-selected={i === idx}
            onClick={() => {
              setIdx(i);
              if (typeof window !== 'undefined' && window.history && m.key) {
                try { window.history.replaceState(null, '', `#${m.key}`); } catch (_) {}
              }
            }}
            className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
              i === idx
                ? 'text-white shadow-lg -translate-y-0.5'
                : 'bg-white text-[#5E6D66] border border-[#E8E6E1] hover:border-[#2D5A4C]/40 hover:text-[#1A2421]'
            }`}
            style={i === idx ? { background: m.accent, boxShadow: `0 8px 20px -6px ${m.accent}88` } : {}}
            data-testid={`mission-tab-${m.key}`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: i === idx ? '#fff' : m.accent }} />
            {m.name}
          </button>
        ))}
      </div>

      {/* Carte mission active */}
      <div
        key={mission.key}
        className="relative rounded-3xl overflow-hidden shadow-2xl motion-fade-up"
        data-testid={`mission-card-${mission.key}`}
      >
        {/* Bandeau header : illustration officielle + overlay gradient */}
        <div className="relative text-white overflow-hidden">
          {/* Image illustration en fond */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${mission.image})` }}
            aria-hidden="true"
            data-testid="mission-hero-image"
          />
          {/* Overlay gradient coloré pour lisibilité + branding */}
          <div className={`absolute inset-0 bg-gradient-to-br ${mission.gradient}`} aria-hidden="true" />

          <div className="relative px-6 md:px-10 py-10 md:py-14 min-h-[280px] md:min-h-[320px] flex flex-col justify-end">
            <p className="text-[10px] uppercase tracking-[0.32em] font-bold opacity-90">Mission coopérative C.I.P.A.S.C.O.P.</p>
            <h3 className="mt-1 text-3xl md:text-5xl font-black leading-tight drop-shadow-md" style={{ fontFamily: 'Manrope' }} data-testid="mission-name">
              {mission.name}
            </h3>
            <p className="mt-1 text-base md:text-lg font-semibold opacity-95 drop-shadow" data-testid="mission-tagline">
              {mission.tagline}
            </p>
            <p className="mt-3 text-sm md:text-base opacity-95 max-w-2xl leading-relaxed drop-shadow">
              {mission.description}
            </p>

            {/* Avatars équipe issus de l'illustration officielle */}
            <div className="mt-5 flex items-center gap-3" data-testid="mission-team-stack">
              <div className="flex -space-x-3">
                {mission.steps.map((s, i) => (
                  <IllustrationAvatar
                    key={i}
                    src={mission.image}
                    cx={s.crop_cx ?? s.crop?.cx ?? 50}
                    cy={s.crop_cy ?? s.crop?.cy ?? 50}
                    zoom={5.5}
                    size={44}
                    ring="#ffffff"
                    testId={`mission-team-avatar-${i}`}
                  />
                ))}
              </div>
              <div className="text-xs">
                <p className="font-bold uppercase tracking-widest">Équipe</p>
                <p className="opacity-90">{mission.steps.length} coopérateurs</p>
              </div>
            </div>
          </div>

          {/* Barre de progression autoplay */}
          <div className="absolute bottom-0 left-0 h-1 w-full bg-white/15 overflow-hidden">
            <div
              key={`${mission.key}-${idx}-${playing}-${reducedMotion}`}
              className="h-full bg-white/90 origin-left"
              style={{
                animation: playing && !reducedMotion ? `mission-progress ${AUTOPLAY_MS}ms linear forwards` : 'none',
                transform: playing && !reducedMotion ? undefined : 'scaleX(0)',
              }}
              data-testid="mission-progress-bar"
            />
          </div>
        </div>

        {/* Étapes */}
        <div className="bg-white px-4 md:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {mission.steps.map((step, i) => {
              const Icon = typeof step.icon === 'string' ? resolveIcon(step.icon) : (step.icon || Sparkle);
              return (
                <div
                  key={i}
                  className="group relative rounded-2xl border border-[#E8E6E1] bg-[#F9F8F5]/60 p-5 hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 motion-fade-up"
                  style={{ animationDelay: `${i * 100}ms` }}
                  data-testid={`mission-step-${i}`}
                >
                  <span
                    className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-white border border-[#E8E6E1] rounded-full px-2 py-0.5"
                    style={{ color: mission.accent }}
                  >
                    Étape {i + 1}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md mb-3 transition-transform group-hover:scale-110 group-hover:rotate-3"
                    style={{ background: mission.accent }}
                  >
                    <Icon size={22} weight="fill" />
                  </div>
                  <p className="text-sm font-extrabold text-[#1A2421] leading-tight" style={{ fontFamily: 'Manrope' }}>
                    {step.label}
                  </p>
                  <p className="mt-1 text-xs text-[#5E6D66] leading-snug">{step.short}</p>

                  <div className="mt-4 flex items-center gap-2 pt-3 border-t border-[#E8E6E1]">
                    <IllustrationAvatar
                      src={mission.image}
                      cx={step.crop_cx ?? step.crop?.cx ?? 50}
                      cy={step.crop_cy ?? step.crop?.cy ?? 50}
                      zoom={6}
                      size={36}
                      ring={mission.accent}
                      testId={`mission-step-avatar-${i}`}
                    />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-semibold">Coopérateur</p>
                      <p className="text-xs font-bold text-[#1A2421] truncate">{step.name}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer badge */}
          <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-3 rounded-xl bg-[#1A2421] text-white px-5 py-3">
            <p className="text-xs md:text-sm font-semibold text-center md:text-left">
              Mission organisée et facturée par <span className="text-[#80C51F] font-bold">OBJECTIF SCOP OUTREMER</span> — apport en industrie de nos associés coopérateurs.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPlaying((p) => !p)}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label={playing ? 'Pause du défilement' : 'Reprendre le défilement'}
                data-testid="mission-play-pause"
              >
                {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
              </button>
              <button
                onClick={prev}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Mission précédente"
                data-testid="mission-prev"
              >
                <CaretLeft size={14} weight="bold" />
              </button>
              <span className="text-xs font-mono opacity-80" data-testid="mission-counter">
                {String(idx + 1).padStart(2, '0')} / {String(missions.length).padStart(2, '0')}
              </span>
              <button
                onClick={next}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Mission suivante"
                data-testid="mission-next"
              >
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Micro-stats sous la carte */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { v: String(missions.length), l: 'Secteurs actifs' },
          { v: String(missions.reduce((a, m) => a + (m.steps?.length || 0), 0)), l: 'Étapes de mission' },
          { v: '100 %', l: 'Facturation coopérative' },
          { v: 'DOM-TOM', l: 'Territoires desservis' },
        ].map((s, i) => (
          <div
            key={i}
            className="rounded-xl border border-[#E8E6E1] bg-white/70 backdrop-blur px-4 py-3 text-center"
            data-testid={`mission-stat-${i}`}
          >
            <p className="text-lg md:text-xl font-extrabold text-[#2D5A4C]" style={{ fontFamily: 'Manrope' }}>{s.v}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold">{s.l}</p>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes mission-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
